import { auth, db } from "../config/firebase.config.js";
import { collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs } from "firebase/firestore";
import { generateCacheKey } from "../audioCache.js";
import { extractTopicGeoContext } from "../utils/topicGeoContext.js";

// Helper to get location name from Nominatim data
const getLocationName = (locationData) => {
  if (!locationData?.address) return 'Unknown Location';
  const addr = locationData.address;
  return addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state || addr.country || 'Unknown Location';
};

// Helper to create simplified location object
const createLocationObject = (locationData, mapCenter = null) => {
  const name = getLocationName(locationData);
  
  // Use locationData coordinates if available, otherwise fall back to map center
  let lat, lng;
  if (locationData?.lat && locationData?.lon) {
    // Nominatim returns lat/lon as strings → convert to numbers for Firestore rules
    lat = parseFloat(locationData.lat);
    lng = parseFloat(locationData.lon);
  } else if (mapCenter) {
    lat = typeof mapCenter.lat === 'number' ? mapCenter.lat : parseFloat(mapCenter.lat);
    lng = typeof mapCenter.lng === 'number' ? mapCenter.lng : parseFloat(mapCenter.lng);
  } else {
    console.warn('[HistoryLogger] No coordinates available for location');
    return null;
  }

  // Guard against NaN values after parsing
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    console.warn('[HistoryLogger] Invalid coordinates after parsing');
    return null;
  }

  const geoContext = extractTopicGeoContext(locationData);

  return {
    name,
    lat,
    lng,
    topicCountry: geoContext.country || '',
    topicSecondMostZoomedOutFeature: geoContext.secondMostZoomedOutFeature || '',
  };
};



// Check if we should skip logging (deduplication)
const shouldSkipLogging = async (userId, type, topic, mode = null) => {
  try {
    // Check last event within 30 seconds
    const historyRef = collection(db, 'users', userId, 'history');
    const recentQuery = query(historyRef, orderBy('timestamp', 'desc'), limit(1));
    const snapshot = await getDocs(recentQuery);
    
    if (!snapshot.empty) {
      const lastEvent = snapshot.docs[0].data();
      const lastEventTime = lastEvent.timestamp.toDate();
      const now = new Date();
      const timeDiff = now - lastEventTime;
      
      // Skip if same type and topic within 30 seconds
      if (timeDiff < 30000) {
        const lastTopic = lastEvent.type === 'audio_call' 
          ? lastEvent.audioCall?.topic 
          : lastEvent.type === 'article_visit'
          ? lastEvent.article?.title
          : lastEvent.location?.name;
        
        const lastMode = lastEvent.audioCall?.mode;
        
        if (lastTopic === topic && 
            (type !== 'audio_call' || lastMode === mode)) {
          console.log(`[HistoryLogger] Skipping duplicate ${type} for "${topic}" (within 30s)`);
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('[HistoryLogger] Error checking recent events:', error);
    return false; // On error, proceed with logging
  }
};

// Main logging function
const logEvent = async (eventData) => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      console.warn('[HistoryLogger] User not authenticated, skipping history log');
      return;
    }

    const userId = user.uid;
    const historyRef = collection(db, 'users', userId, 'history');
    
    // Add timestamp
    const eventWithTimestamp = {
      ...eventData,
      timestamp: serverTimestamp()
    };
    
    await addDoc(historyRef, eventWithTimestamp);
    console.log(`[HistoryLogger] Logged ${eventData.type}:`, eventWithTimestamp);
    
  } catch (error) {
    console.error('[HistoryLogger] Error logging event:', error);
  }
};

// Public API functions

export const logLocationVisit = async (locationData, mapCenter = null) => {
  const location = createLocationObject(locationData, mapCenter);
  if (!location) return;

  if (!auth.currentUser) return;

  // Check for duplicates
  const shouldSkip = await shouldSkipLogging(auth.currentUser.uid, 'location_visit', location.name);
  if (shouldSkip) return;

  await logEvent({
    type: 'location_visit',
    location
  });
};

export const logArticleVisit = async (article, currentLocationData, mapCenter = null) => {
  if (!article?.title || !article?.pageid) return;
  
  const location = createLocationObject(currentLocationData, mapCenter);
  if (!location) return;

  if (!auth.currentUser) return;

  // Check for duplicates
  const shouldSkip = await shouldSkipLogging(auth.currentUser.uid, 'article_visit', article.title);
  if (shouldSkip) return;

  await logEvent({
    type: 'article_visit',
    location,
    article: {
      pageid: article.pageid.toString(),
      title: article.title,
      lat: typeof article.lat === 'number' ? article.lat : 0,
      lng: typeof article.lon === 'number' ? article.lon : 0
    }
  });
};

export const logAudioCall = async (topic, mode, currentLocationData, article = null, parameters = null, userLanguage = 'English', mapCenter = null) => {
  if (!topic || !mode) return;

  const location = createLocationObject(currentLocationData, mapCenter);
  if (!location) return;

  if (!auth.currentUser) return;

  // Check for duplicates (allow repeated anecdote requests so users can ask for more)
  if (mode !== 'anecdote') {
    const shouldSkip = await shouldSkipLogging(auth.currentUser.uid, 'audio_call', topic, mode);
    if (shouldSkip) return;
  }

  const forcedCacheKey = parameters?.forceCacheKey ? String(parameters.forceCacheKey).trim() : '';
  const cacheKey = forcedCacheKey || generateCacheKey(topic, mode, parameters, userLanguage);
  const geoContext = extractTopicGeoContext(currentLocationData);

  const eventData = {
    type: 'audio_call',
    location,
    audioCall: {
      mode,
      topic,
      topicCountry: geoContext.country || '',
      topicSecondMostZoomedOutFeature: geoContext.secondMostZoomedOutFeature || '',
      userLanguage,
      cacheKey
    }
  };

  // Add article info if the audio is about an article
  if (article) {
    eventData.article = {
      pageid: article.pageid?.toString() || '',
      title: article.title || '',
      lat: typeof article.lat === 'number' ? article.lat : 0,
      lng: typeof article.lon === 'number' ? article.lon : 0
    };
  }

  await logEvent(eventData);
};

// Helper to get user's history (for future BrowsingHistory component)
export const getUserHistory = async (limitCount = 50) => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const historyRef = collection(db, 'users', user.uid, 'history');
    const historyQuery = query(historyRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(historyQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error('[HistoryLogger] Error fetching user history:', error);
    throw error;
  }
}; 